import type {
  ActionEffects,
  ActionId,
  ClueId,
  SeedChoice,
  SortCardId,
  SortValue,
  StoryLine,
  WitnessEffects,
  WitnessId,
} from '../types/demo'
import { AMARA_MENTOR } from './speakers'

export const seedOptions: { id: SeedChoice; label: string }[] = [
  { id: 'leaf', label: 'Acacia leaf' },
  { id: 'water', label: 'Water drop' },
  { id: 'stone', label: 'Small stone' },
  { id: 'blank', label: 'Leave blank' },
]

export const arrivalReplies: { id: string; label: string; response: string }[] = [
  { id: 'here', label: 'I’m here.', response: 'Here is enough.' },
  { id: 'info', label: 'I need more info.', response: 'We check things before we decide what they mean.' },
  { id: 'unsure', label: 'I’m not sure.', response: 'Good answer. Keep it.' },
]

export const arrivalRecap =
  'Monkey waited by water. Meerkats made a strange sound. Panther watched grass. Amara heard a hiss and old school whispers at once.'

export interface Clue {
  id: ClueId
  name: string
  position: { x: number; y: number }
  lines: StoryLine[]
  observation: string
}

export const clues: Clue[] = [
  {
    id: 'monkey',
    name: 'Monkey',
    position: { x: 23, y: 66 },
    lines: [
      { kind: 'narration', text: 'Monkey takes one step toward water, then stops.' },
      { kind: 'inner', speaker: 'Monkey', text: 'If I turn back, they will see I was scared.' },
    ],
    observation: 'Monkey is thirsty and alone.',
  },
  {
    id: 'meerkats',
    name: 'Meerkats',
    position: { x: 65, y: 44 },
    lines: [
      { kind: 'speech', speaker: 'Meerkat', text: 'Did you hear?' },
      { kind: 'speech', speaker: 'Meerkat', text: 'I only said it again.' },
      { kind: 'speech', speaker: 'Meerkat', text: 'Maybe danger.' },
    ],
    observation: 'The group repeats a sound before checking it.',
  },
  {
    id: 'panther',
    name: 'Panther',
    position: { x: 83, y: 80 },
    lines: [],
    observation: 'Panther watches one spot in the grass. Panther is not following Monkey.',
  },
  {
    id: 'water',
    name: 'Water',
    position: { x: 38, y: 86 },
    lines: [],
    observation: 'A side-water path runs under the roots. It is hard to see through the fog.',
  },
  {
    id: 'grass',
    name: 'Grass',
    position: { x: 87, y: 58 },
    lines: [{ kind: 'sound', text: 'A hiss from the grass.' }],
    observation: 'A reed bends near the old pipe. The source is not clear yet.',
  },
  {
    id: 'amara',
    name: 'Amara',
    position: { x: 44, y: 51 },
    lines: [
      { kind: 'narration', text: 'Her hands are tight. Her voice feels stuck.' },
      {
        kind: 'speech',
        speaker: AMARA_MENTOR,
        text: 'My brain thinks every laugh has teeth. It is trying to help. It is also making evidence soup.',
      },
    ],
    observation: 'Alarm is loud. Facts are not clear.',
  },
]

export const cluesBrainWorld =
  'A brain under pressure may look for danger fast. A startled herd may react to every rustle. Both need more than one clue.'

export const sortValueLabels: Record<SortValue, string> = {
  helpNow: 'Needs help now',
  moreFacts: 'Needs more facts',
  canWait: 'Can wait',
}

export interface SortCard {
  id: SortCardId
  label: string
  feedback: Record<SortValue, string>
}

export const sortCards: SortCard[] = [
  {
    id: 'hiss',
    label: 'Hiss in grass',
    feedback: {
      helpNow: 'A strange sound near water may need care. Care can mean watch, step back, or get help.',
      moreFacts: 'You do not know the source yet. Check from a safer place.',
      canWait: 'This could matter near water. You can revise or ask for help checking.',
    },
  },
  {
    id: 'meerkats',
    label: 'Meerkat sounds',
    feedback: {
      helpNow: 'You can respond to the pressure. Ask the group to stop repeating while someone checks.',
      moreFacts: 'A group can be loud and still be wrong. Repeating a thing does not make it true.',
      canWait: 'You do not have to answer every sound. The repeating may still affect Monkey.',
    },
  },
  {
    id: 'monkey',
    label: 'Monkey alone',
    feedback: {
      helpNow: 'Support can be a path, an ally, or someone nearby.',
      moreFacts: 'You can ask what support would help. Monkey does not have to explain everything.',
      canWait: 'Monkey can choose distance. You can still keep a path open.',
    },
  },
  {
    id: 'heart',
    label: 'Amara’s fast heart',
    feedback: {
      helpNow: 'Her body says this matters. A pause or smaller step may help.',
      moreFacts: 'A fast heart is one clue. It does not prove every fear is a fact.',
      canWait: 'Amara can choose her pace. Keep a pause available.',
    },
  },
  {
    id: 'bird',
    label: 'Distant bird call',
    feedback: {
      helpNow: 'If it connects to the warning, get help checking. You do not need to rush toward it.',
      moreFacts: 'You can check if it is part of this scene.',
      canWait: 'Some sounds can wait. Keeping attention free is a skill too.',
    },
  },
]

export const sortExample: Record<SortCardId, SortValue> = {
  hiss: 'moreFacts',
  meerkats: 'moreFacts',
  monkey: 'helpNow',
  heart: 'helpNow',
  bird: 'canWait',
}

export interface StoryAction {
  id: ActionId
  label: string
  summary: string
  note?: string
  lines: StoryLine[]
  feedback: string
  effects: ActionEffects
  sceneDescription: string
}

export const storyActions: StoryAction[] = [
  {
    id: 'watch',
    label: 'Watch from a safe spot',
    summary: 'Stay near the roots and look again.',
    lines: [{ kind: 'speech', speaker: AMARA_MENTOR, text: 'I can stay near the roots and look again.' }],
    feedback:
      'Watching can give more facts. It has not opened Monkey’s path yet. You can still ask for help.',
    effects: { grassObserved: true },
    sceneDescription:
      'From the roots, Panther’s gaze is easier to follow: it points at the grass. Monkey still waits at the edge. Some fog stays.',
  },
  {
    id: 'signal',
    label: 'Send Monkey a small signal',
    summary: 'A quiet sign that someone noticed.',
    lines: [
      { kind: 'sound', text: 'A quiet click.' },
      { kind: 'speech', speaker: AMARA_MENTOR, text: 'Tiny signals still count.' },
    ],
    feedback: 'A small sign can reduce the alone feeling. Monkey does not have to reply.',
    effects: { smallSignalSent: true },
    sceneDescription:
      'Monkey turns toward Amara. A thin dotted line now connects them.',
  },
  {
    id: 'sidePath',
    label: 'Use the side-water path',
    summary: 'Step out of the middle and take another route.',
    lines: [
      { kind: 'narration', text: 'Amara steps toward the acacia roots.' },
      { kind: 'speech', speaker: AMARA_MENTOR, text: 'Leaving the middle is not losing. It is route math.' },
    ],
    feedback: 'Space can help Amara check the clues. The side path gives another choice.',
    effects: { sideRouteVisible: true },
    sceneDescription:
      'The side channel under the roots now has a clear path outline and a label: Side-water path.',
  },
  {
    id: 'panther',
    label: 'Trust Panther with care',
    summary: 'Watch what Panther is actually doing.',
    note: 'A story-world animal moment. Not advice for real wildlife.',
    lines: [
      {
        kind: 'narration',
        text: 'Amara watches what Panther is doing. Panther stays focused on the grass.',
      },
      { kind: 'sound', text: 'Amara makes a soft purr sound. Panther answers.' },
      { kind: 'speech', speaker: AMARA_MENTOR, text: 'Panther is doing better detective work than I am.' },
    ],
    feedback: 'Trust grows when actions match the clues. This does not tell us the whole story yet.',
    effects: { pantherObservedWithCare: true },
    sceneDescription: 'Panther stays low, eyes on the grass near the old pipe. A soft line marks where Panther looks.',
  },
]

export interface WitnessOption {
  id: WitnessId
  label: string
  lines: StoryLine[]
  effects: WitnessEffects
  sceneDescription: string
}

export const witnessOptions: WitnessOption[] = [
  {
    id: 'antelope',
    label: 'Ask Antelope to join.',
    lines: [{ kind: 'speech', speaker: 'Giraffe', text: 'I do not have to cross alone.' }],
    effects: { allyJoined: true },
    sceneDescription: 'Antelope now stands beside Giraffe in the shade.',
  },
  {
    id: 'sidePath',
    label: 'Make the side path clear.',
    lines: [{ kind: 'speech', speaker: 'Giraffe', text: 'Not a spotlight. Just a way through.' }],
    effects: { witnessSideRouteVisible: true },
    sceneDescription: 'Giraffe turns a path sign toward Monkey. The side-water path is easier to see.',
  },
  {
    id: 'sentinel',
    label: 'Call Sentinel.',
    lines: [{ kind: 'speech', speaker: 'Elephant Sentinel', text: 'We do not have to guess alone.' }],
    effects: { sentinelCalled: true },
    sceneDescription: 'Elephant Sentinel now stands near the edge of the grass.',
  },
  {
    id: 'skip',
    label: 'Skip.',
    lines: [{ kind: 'narration', text: 'You leave this one for now. Nothing is lost.' }],
    effects: {},
    sceneDescription: 'The scene stays as it was.',
  },
]

export const echoLines: string[] = [
  'Monkey was afraid.',
  'Giraffe was unsure.',
  'Panther was listening.',
  'Meerkats were repeating.',
  'Amara was overwhelmed.',
  'The hiss was still there.',
]

export const echoTitle = 'The silence was not one thing.'
